// Phase 36 Batch X — FX-Adjusted Returns panel on Deal Detail.
//
// Rendered ONLY when `deal.fx_view.fx_applicable === true` (i.e. the
// deal's property_currency differs from the investor's base currency).
// Same-currency deals hide this panel entirely — there's nothing
// meaningful to show.
//
// Premium chrome to match the rest of the deal detail page (white bg,
// slate text, indigo accents, soft shadows, rounded-2xl). Mobile-first
// grid that collapses 1-col on small screens.

import { Card } from "@/components/ui/card";
import {
  Coins,
  TrendingUp,
  TrendingDown,
  ArrowRightLeft,
  AlertTriangle,
} from "lucide-react";
import { CurrencyRiskBadgeWithDetail } from "./CurrencyRiskBadge";

const FLAG_BY_CCY = {
  USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵", CAD: "🇨🇦",
  AUD: "🇦🇺", CHF: "🇨🇭", CNY: "🇨🇳", MXN: "🇲🇽", BRL: "🇧🇷",
  COP: "🇨🇴", ARS: "🇦🇷", CLP: "🇨🇱", PEN: "🇵🇪", DOP: "🇩🇴",
  INR: "🇮🇳", ZAR: "🇿🇦", SGD: "🇸🇬", HKD: "🇭🇰", SEK: "🇸🇪",
};

function flag(ccy) {
  return FLAG_BY_CCY[(ccy || "").toUpperCase()] || "🌐";
}

function fmtMoney(n, ccy) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  // Locale-aware currency rendering. Bypass fmt.money so we can
  // pass the actual currency code (USD vs COP vs EUR).
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: ccy || "USD",
      maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return `${ccy || ""} ${Number(n).toLocaleString()}`;
  }
}

function fmtPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const sign = Number(n) >= 0 ? "+" : "";
  return `${sign}${(Number(n) * 100).toFixed(2)}%`;
}

function ReturnRow({ label, value, hint }) {
  const positive = (value ?? 0) >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const tone = positive
    ? "text-emerald-700 bg-emerald-50 border-emerald-100"
    : "text-rose-700 bg-rose-50 border-rose-100";
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {hint ? <p className="mt-0.5 text-xs text-slate-500">{hint}</p> : null}
      </div>
      <div
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-semibold tabular-nums ${tone}`}
      >
        <Icon className="h-3.5 w-3.5" />
        {fmtPct(value)}
      </div>
    </div>
  );
}

export default function FXAdjustedReturnsPanel({ fxView, currencyRisk }) {
  // Caller passes the deal's `fx_view`. Same-currency or missing →
  // render nothing. The Deal Detail page is responsible for not
  // showing a section header / wrapper when this returns null.
  if (!fxView || !fxView.fx_applicable) return null;

  const {
    property_currency,
    base_currency,
    purchase_rate,
    current_rate,
    rate_stale,
    rate_source,
    property_return_pct,
    currency_return_pct,
    total_return_pct,
    purchase_value_in_base_currency,
    current_value_in_base_currency,
  } = fxView;

  const rateMovedPct = purchase_rate && current_rate
    ? (purchase_rate / current_rate - 1) * 100
    : 0;
  const rateMoveTone = rateMovedPct >= 0 ? "text-emerald-600" : "text-rose-600";

  return (
    <Card className="overflow-hidden" data-testid="fx-adjusted-returns-panel">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/60 px-5 py-3">
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-900">
            FX-Adjusted Returns
          </h2>
        </div>
        <div
          className="inline-flex flex-wrap items-center gap-2"
          data-testid="fx-header-trailing"
        >
          <div
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700"
            data-testid="fx-chip"
          >
            <span aria-hidden="true">{flag(property_currency)}</span>
            <span>{property_currency}</span>
            <ArrowRightLeft className="h-3 w-3 text-slate-400" />
            <span aria-hidden="true">{flag(base_currency)}</span>
            <span>{base_currency}</span>
          </div>
          {/* Phase 36 Batch Y — currency risk pill. Pulled by Deal Detail
              from /api/fx/risk and forwarded as a prop. Hidden when not
              provided (e.g. risk endpoint failed).
              Phase 36 Batch Z — wrapped in the WithDetail variant which
              adds a "View detailed risk" expander showing the 4 driver
              sparklines from /api/fx/risk/history. */}
          {currencyRisk ? (
            <CurrencyRiskBadgeWithDetail
              bucket={currencyRisk.bucket}
              score={currencyRisk.risk_score}
              factors={currencyRisk.factors}
              currency={property_currency}
              coverage={currencyRisk.data_coverage}
              userBase={base_currency}
            />
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 px-5 py-5 sm:grid-cols-2">
        {/* Left column — absolute values in base currency */}
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Cost basis ({base_currency})
            </p>
            <p
              className="mt-0.5 text-2xl font-semibold text-slate-900 tabular-nums"
              data-testid="fx-purchase-value-base"
            >
              {fmtMoney(purchase_value_in_base_currency, base_currency)}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Locked at {fmtMoney(1, base_currency)} = {Number(purchase_rate).toLocaleString()} {property_currency}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Current value ({base_currency})
            </p>
            <p
              className="mt-0.5 text-2xl font-semibold text-slate-900 tabular-nums"
              data-testid="fx-current-value-base"
            >
              {fmtMoney(current_value_in_base_currency, base_currency)}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              At today's rate: {fmtMoney(1, base_currency)} = {Number(current_rate).toLocaleString()} {property_currency}
              <span className={`ml-1 font-medium ${rateMoveTone}`}>
                ({rateMovedPct >= 0 ? "+" : ""}{rateMovedPct.toFixed(2)}%)
              </span>
            </p>
          </div>
        </div>

        {/* Right column — return decomposition */}
        <div className="rounded-xl border border-slate-100 bg-slate-50/40 px-4 py-2">
          <ReturnRow
            label="Property return"
            value={property_return_pct}
            hint={`Appreciation in ${property_currency} only`}
          />
          <div className="border-t border-slate-100" />
          <ReturnRow
            label="Currency return"
            value={currency_return_pct}
            hint={`FX move ${property_currency} → ${base_currency}`}
          />
          <div className="border-t border-slate-200" />
          <div className="flex items-center justify-between gap-3 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Total return</p>
              <p className="text-xs text-slate-500">Compound · property × currency</p>
            </div>
            <div
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-base font-bold tabular-nums ${
                (total_return_pct ?? 0) >= 0
                  ? "bg-indigo-600 text-white"
                  : "bg-rose-600 text-white"
              }`}
              data-testid="fx-total-return"
            >
              {fmtPct(total_return_pct)}
            </div>
          </div>
        </div>
      </div>

      {rate_stale ? (
        <div className="flex items-center gap-2 border-t border-amber-200 bg-amber-50/60 px-5 py-2 text-xs text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5 flex-none" />
          Rate may be outdated — couldn't refresh from the FX provider
          ({rate_source}). Returns are computed from the last cached value.
        </div>
      ) : null}
    </Card>
  );
}
